import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Mic, ArrowRight, BookOpen, Mail, Headphones, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import PodcastBand from "@/components/PodcastBand";

type Episode = {
  title: string;
  series: string;
  platform: string;
  url: string;
  image: string;
  description: string;
  topics: string[];
  isVideo?: boolean;
};

const episodes: Episode[] = [
  {
    title: "OT-Security in der Gebäudeautomation: Expert Talk mit Tim Bauer",
    series: "EXPERT TALK",
    platform: "YouTube",
    url: "https://www.youtube.com/watch?v=8OGBuOqRq34",
    image: "/podcast-expert-talk-ot-security.jpg",
    description:
      "Expert Talk mit ondeso über OT-Security in der Gebäudeautomation: Moderne Gebäude sind stark vernetzt, Heizung, Lüftung, Zutritt, Beleuchtung und Brandmeldetechnik greifen auf Steuerungen, Sensoren, Aktoren und klassische IT-Systeme zurück. Warum Gebäudeautomation echte OT ist und wie Betreiber sie souverän absichern.",
    topics: ["OT-Security", "Gebäudeautomation", "Expert Talk"],
    isVideo: true,
  },
  {
    title: "RADIO OT: OT-Security in der Gebäudeautomation",
    series: "RADIO OT",
    platform: "YouTube",
    url: "https://www.youtube.com/watch?v=88wgcJg9S6k",
    image: "https://img.youtube.com/vi/88wgcJg9S6k/maxresdefault.jpg",
    description:
      "Warum OT-Security in der Gebäudeautomation anders funktioniert als klassische IT-Sicherheit: über Verantwortlichkeiten zwischen Facility Management und IT, Zonen und Conduits nach IEC 62443 und die Frage, wo Betreiber realistisch anfangen.",
    topics: ["OT-Security", "Gebäudeautomation", "IEC 62443"],
    isVideo: true,
  },
  {
    title: "RADIO OT: Zwischen IT und Betriebstechnik",
    series: "RADIO OT",
    platform: "Spotify",
    url: "https://open.spotify.com/episode/285KC5fCi9epUHFNsT8btR",
    image: "/podcast-radio-ot-spotify.png",
    description:
      "Gespräche über die Praxis zwischen IT und Betriebstechnik: Assetmanagement, Netzwerksegmentierung, Fernzugriffe von Dienstleistern und der Aufbau einer OT-Organisation, die den Betrieb nicht ausbremst.",
    topics: ["OT-Organisation", "OT-Asset-Management", "Netzwerkarchitektur"],
  },
  {
    title: "OT SECURITY MADE SIMPLE: IEC 62443 als Betriebsmodell",
    series: "OT SECURITY MADE SIMPLE",
    platform: "Spotify",
    url: "https://open.spotify.com/episode/0oS20cfp6loGrBmMYt2dpA",
    image: "/podcast-ot-security-made-simple.png",
    description:
      "IEC 62443-basierte OT-Security als Betriebsmodell für Betreiber von Gebäudeautomation und OT-Organisationen: von Schutzzielen und Security Levels über Rollen und Prozesse bis zur Frage, wie NIS-2 und KRITIS-Anforderungen darin aufgehen.",
    topics: ["IEC 62443", "NIS-2", "Betreiberverantwortung"],
  },
  {
    title: "BACnet Secure Connect: Der Heilsbringer für smarte Gebäude im Realitätscheck",
    series: "OT SECURITY MADE SIMPLE",
    platform: "Spotify",
    url: "https://open.spotify.com/episode/1nRZv2EUvlpY1yByR3cLyX",
    image: "/podcast-bacnet-sc-ot-security-made-simple.jpg",
    description:
      "Realitätscheck zu BACnet/SC: Was die sichere Transportvariante von BACnet in smarten Gebäuden wirklich löst, welche Zertifikats- und Migrationsfragen Betreiber unterschätzen und wie ein Umstieg schrittweise gelingt.",
    topics: ["BACnet/SC", "Building Automation", "Migration"],
  },
  {
    title: "SECURING INDUSTRY: Schutz kritischer Infrastrukturen",
    series: "SECURING INDUSTRY",
    platform: "YouTube",
    url: "https://www.youtube.com/watch?v=fgQ49aQuyp0",
    image: "https://img.youtube.com/vi/fgQ49aQuyp0/maxresdefault.jpg",
    description:
      "Praxisnahe Einblicke in Industrial Security: wie Betreiber kritischer Infrastrukturen Risiken bewerten, Prioritäten setzen und Maßnahmen umsetzen, die im laufenden Betrieb tragfähig bleiben.",
    topics: ["Industrial Security", "KRITIS", "Risikobewertung"],
    isVideo: true,
  },
];

const topicLinks = [
  { to: "/iec62443", label: "IEC 62443 in der Gebäudeautomation" },
  { to: "/iec62443-goldstandard", label: "IEC 62443 als Goldstandard" },
  { to: "/nis2", label: "NIS-2 für Betreiber" },
  { to: "/bacnet-sc-migration", label: "BACnet/SC Migration" },
  { to: "/ot-asset-management", label: "OT-Asset-Management" },
  { to: "/ot-organisation", label: "OT-Organisation aufbauen" },
  { to: "/ot-netzwerkarchitektur", label: "OT-Netzwerkarchitektur" },
  { to: "/ot-security-krankenhaus", label: "OT-Security im Krankenhaus" },
];

const Podcasts = () => {
  const openContact = () => window.dispatchEvent(new CustomEvent("open-contact-form"));
  const canonical = "https://fachsprechpartner.de/podcasts";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "name": "OT-Security Podcast: IEC 62443, Gebäudeautomation und BACnet/SC",
        "url": canonical,
        "inLanguage": "de-DE",
        "description":
          "Podcast-Episoden mit Tim Bauer zu OT-Security, IEC 62443, Gebäudeautomation, Building Automation, BACnet/SC, NIS-2 und KRITIS.",
        "about": [
          { "@type": "Thing", "name": "OT-Security" },
          { "@type": "Thing", "name": "IEC 62443" },
          { "@type": "Thing", "name": "Gebäudeautomation" },
          { "@type": "Thing", "name": "Building Automation" },
          { "@type": "Thing", "name": "BACnet Secure Connect" },
          { "@type": "Thing", "name": "NIS-2" },
        ],
        "mainEntity": {
          "@type": "ItemList",
          "numberOfItems": episodes.length,
          "itemListElement": episodes.map((e, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "PodcastEpisode",
              name: e.title,
              url: e.url,
              inLanguage: "de-DE",
              image: e.image.startsWith("http") ? e.image : `https://fachsprechpartner.de${e.image}`,
              description: e.description,
              keywords: e.topics.join(", "),
              partOfSeries: { "@type": "PodcastSeries", name: e.series },
              author: { "@type": "Person", name: "Tim Bauer", url: "https://fachsprechpartner.de" },
              associatedMedia: {
                "@type": "AudioObject",
                contentUrl: e.url,
                name: e.title,
                description: e.description,
              },
            },
          })),
        },
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fachsprechpartner.de/" },
          { "@type": "ListItem", position: 2, name: "Podcasts", item: canonical },
        ],
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>OT-Security Podcast: IEC 62443, Gebäudeautomation, BACnet/SC</title>
        <meta
          name="description"
          content="Podcast-Episoden zu OT-Security, IEC 62443, Gebäudeautomation und BACnet/SC: RADIO OT, OT Security made simple und Securing Industry mit Tim Bauer, auf Spotify und YouTube."
        />
        <meta
          name="keywords"
          content="OT-Security Podcast, IEC 62443 Podcast, Gebäudeautomation Podcast, Building Automation Security, BACnet Secure Connect, NIS-2 Podcast, KRITIS, Radio OT, Industrial Security Podcast"
        />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="de" href={canonical} />
        <link rel="alternate" hrefLang="x-default" href={canonical} />
        <meta property="og:title" content="OT-Security Podcast: IEC 62443, Gebäudeautomation, BACnet/SC" />
        <meta
          property="og:description"
          content="Podcast-Episoden zu OT-Security, IEC 62443, Gebäudeautomation und BACnet/SC mit Tim Bauer, auf Spotify und YouTube."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content="https://fachsprechpartner.de/og-image.webp" />
        <meta property="og:image:type" content="image/webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="FACHSPRECHPARTNER Podcasts: OT-Security und Gebäudeautomation" />
        <meta property="og:locale" content="de_DE" />
        <meta property="og:site_name" content="Fachsprechpartner" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OT-Security Podcast: IEC 62443, Gebäudeautomation, BACnet/SC" />
        <meta
          name="twitter:description"
          content="Podcast-Episoden zu OT-Security, IEC 62443, Gebäudeautomation und BACnet/SC mit Tim Bauer."
        />
        <meta name="twitter:image" content="https://fachsprechpartner.de/og-image.webp" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Navbar />

      <main className="min-h-screen bg-background">
        {/* Hero */}
        <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-primary to-primary/90 text-primary-foreground">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 text-accent text-xs font-semibold uppercase tracking-wider mb-6">
                <Headphones className="w-4 h-4" />
                Podcasts
              </div>
              <h1 className="text-4xl md:text-6xl font-heading font-bold leading-tight mb-6">
                OT-Security Podcast: IEC 62443, Gebäudeautomation und BACnet/SC.
              </h1>
              <p className="text-lg md:text-xl text-primary-foreground/85 mb-8 leading-relaxed max-w-3xl">
                In RADIO OT, OT Security made simple und Securing Industry spreche ich mit Experten und
                Betreibern über OT-Security in der Gebäudeautomation und Building Automation:
                praxisnah, technisch und ohne Marketingfloskeln. Alle Episoden auf Spotify und YouTube.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={openContact}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-colors"
                >
                  Als Gast vorschlagen <ArrowRight className="w-4 h-4" />
                </button>
                <Link
                  to="/#speaker"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 text-primary-foreground font-semibold hover:bg-primary-foreground/10 transition-colors"
                >
                  <Mic className="w-4 h-4" />
                  Speaker-Engagement
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Podcast Band */}
        <section className="py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="sr-only">Podcast-Episoden zum Anhören</h2>
            <PodcastBand />
          </div>
        </section>

        {/* Episoden */}
        <section className="py-16 md:py-24 bg-muted/40">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Episoden zu OT-Security, IEC 62443 und Gebäudeautomation
            </h2>
            <p className="text-muted-foreground mb-12 max-w-3xl">
              Drei Formate, die unterschiedliche Facetten von OT-Security beleuchten: Interviews,
              Einzelgespräche und Diskussionen aus der Praxis von Betreibern, Integratoren und
              Herstellern.
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              {episodes.map((e) => (
                <article
                  key={e.url}
                  className="rounded-2xl border border-border bg-card overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <img
                    src={e.image}
                    alt={`Cover der Podcast-Episode ${e.title} über ${e.topics.join(", ")}`}
                    loading="lazy"
                    className="w-full aspect-video object-cover"
                  />
                  <div className="p-6">
                    <p className="text-xs uppercase tracking-wider text-secondary font-semibold mb-2">
                      {e.series} · {e.platform}
                    </p>
                    <h3 className="text-xl font-heading font-semibold mb-3">{e.title}</h3>
                    <p className="text-muted-foreground leading-relaxed mb-4">{e.description}</p>
                    <div className="flex flex-wrap gap-2 mb-5">
                      {e.topics.map((t) => (
                        <span
                          key={t}
                          className="px-2.5 py-1 rounded-full bg-accent/10 text-accent text-xs font-medium"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                    <a
                      href={e.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-accent transition-colors"
                    >
                      Episode auf {e.platform} anhören
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Themen */}
        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Themen aus den Podcasts vertiefen
            </h2>
            <p className="text-muted-foreground mb-8 max-w-3xl">
              Zu den wichtigsten Themen der Episoden finden Sie hier ausführliche Fachbeiträge:
              von der Normenreihe IEC 62443 über NIS-2 bis zur BACnet/SC Migration in der
              Gebäudeautomation.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {topicLinks.map((t) => (
                <Link
                  key={t.to}
                  to={t.to}
                  className="inline-flex items-center justify-between gap-3 px-5 py-4 rounded-xl border border-border bg-card hover:border-accent hover:shadow-md transition-all"
                >
                  <span className="font-medium">{t.label}</span>
                  <ArrowRight className="w-4 h-4 text-accent shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-primary text-primary-foreground">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Selbst aktiv werden?
            </h2>
            <p className="text-primary-foreground/85 mb-8 leading-relaxed">
              Buchen Sie ein Sparring oder sichern Sie sich das Workbook für einen strukturierten
              Einstieg in OT-Security nach IEC 62443.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/workbook"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                Workbook ansehen
              </Link>
              <button
                onClick={openContact}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 font-semibold hover:bg-primary-foreground/10 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Sparring anfragen
              </button>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Podcasts;
